import {
  createClient,
  debugExchange,
  dedupExchange,
  fetchExchange,
  Exchange,
  CombinedError
} from "@urql/core";
import { devtoolsExchange } from "@urql/devtools";
import { retryExchange } from "@urql/exchange-retry";
import { Logger } from "monitoring/BrowserLogs";
import { NewNotification } from "../collections";
import React from "react";
import {
  errorLoggerExchange,
  notifyExchange,
  noopExchange,
  loggerExchange,
  cacheLoggerExchange,
  featureExchange
} from "./exchanges";
import cache from "./cache";
import { GraphqlActions } from "../store";
import { Dispatch } from "redux";
import { IntrospectionQuery } from "graphql";
import introspection from "../generated/introspection.json";
import { ReactSDKClient } from "@optimizely/react-sdk";
import { flagSettings } from "./featuredFields";
import { Auth0ContextInterface } from "@auth0/auth0-react";
import {
  buildAuthExchangeOptions,
  logoutOnBubbledAuthError
} from "./exchanges/auth0AuthenticationExchange";
import { authExchange } from "@urql/exchange-auth";

type ClientConfig = {
  url: string;
  logger: Logger;
  notify: React.Dispatch<NewNotification>;
  auth0: Pick<
    Auth0ContextInterface,
    "getAccessTokenSilently" | "logout" | "getIdTokenClaims"
  >;
  verboseUrqlLogs?: boolean;
  dispatcher: Dispatch<GraphqlActions>;
  optimizely: ReactSDKClient | null;
};

const retryOptions = {
  initialDelayMs: 1000,
  maxDelayMs: 15000,
  randomDelay: true,
  maxNumberAttempts: 2,
  retryIf: (error: CombinedError) => {
    return !!(error && error.networkError);
  }
};

//@ts-ignore
const schema: IntrospectionQuery = introspection as IntrospectionQuery;
/**
 * Builds a client and merges an auth token source into the list of exchanges.
 * @param url
 * @param logger
 * @param notify
 * @param fetch
 * @param verboseUrqlLogs
 */
export const client = ({
  url,
  logger,
  notify,
  optimizely,
  auth0,
  verboseUrqlLogs,
  dispatcher
}: ClientConfig) => {
  const exchanges: Exchange[] = [
    //Deduplicate identical requests from components, eg: useListQueryQuery happening at the same time in many places.
    dedupExchange,
    // Set REACT_APP_VERBOSE_URQL_LOGS to true to get an incredible amout of logging on what is happening withing urql.
    verboseUrqlLogs ? debugExchange : noopExchange,
    // A complex error logger, sends errors to GA/Data Dog
    errorLoggerExchange(logger),
    // Logout if we got an auth error from the server, like "UNAUTHORIZED"
    logoutOnBubbledAuthError(auth0),
    // A proof of concept simple logger that logs slow queries.
    loggerExchange,
    // Remove nullable fields that are tied to feature flags that are off.
    optimizely !== null
      ? featureExchange({
          schema,
          optimizely,
          flags: flagSettings
        })
      : noopExchange,
    //Integrate with https://chrome.google.com/webstore/detail/urql-devtools/mcfphkbpmkbeofnkjehahlmidmceblmm?hl=en-US
    devtoolsExchange,
    // The normalized cache, lots of advance behaviors: https://formidable.com/open-source/urql/docs/graphcache/normalized-caching/
    cache,
    // Connect to the push alerts to tell the users about errors.
    notifyExchange(notify),
    // Try to remember the last 10 or so operations in case of a total UI crash.
    cacheLoggerExchange({
      dispatch: dispatcher
    }),
    // Add auth headers, and do our best to make sure auth is up to date as requests are made.
    authExchange(buildAuthExchangeOptions(auth0)),
    // Retry failures for #cloud_software reasons.
    retryExchange(retryOptions),
    // The actually http request.
    fetchExchange
  ];
  return createClient({
    url: url,
    suspense: false,
    fetchOptions: {
      headers: {
        "apollographql-client-name": "lens-web-ui"
      }
    },
    exchanges
  });
};
